import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, UserPlus, UserCheck, UserX, Users, Clock, ArrowLeft,
  Loader2, Car, Shield, User, Check, X, MessageCircle, Circle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { peopleAPI, friendAPI } from '../lib/api';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import ChatButton from '../components/ChatButton';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { devError } from '../lib/devLog';

const People = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('discover');
  const [searchQuery, setSearchQuery] = useState('');
  const [people, setPeople] = useState([]);
  const [friends, setFriends] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useOnlineStatus();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [friendsRes, receivedRes, sentRes] = await Promise.all([
        friendAPI.getFriends(),
        friendAPI.getReceivedRequests(),
        friendAPI.getSentRequests(),
      ]);
      setFriends(friendsRes.data);
      setReceivedRequests(receivedRes.data);
      setSentRequests(sentRes.data);
    } catch (e) {
      devError(e);
    } finally {
      setLoading(false);
    }
  };

  const searchPeople = useCallback(async (q) => {
    try {
      const res = await peopleAPI.search(q);
      setPeople(res.data);
    } catch (e) {
      devError(e);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchPeople(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchPeople]);

  const handleAction = async (actionId, action) => {
    setActionLoading(prev => ({ ...prev, [actionId]: true }));
    try {
      await action();
      await fetchAll();
      searchPeople(searchQuery);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionId]: false }));
    }
  };

  const getRoleIcon = (role) => {
    if (role === 'driver') return Car;
    if (role === 'admin') return Shield;
    return User;
  };

  const getRoleBadge = (role) => {
    const styles = {
      driver: 'bg-orange-100 text-orange-700',
      admin: 'bg-purple-100 text-purple-700',
      user: 'bg-blue-100 text-blue-700',
    };
    return styles[role] || styles.user;
  };

  const tabs = [
    { id: 'discover', label: 'Discover', icon: Search, count: 0 },
    { id: 'friends', label: 'Friends', icon: Users, count: friends.length },
    { id: 'requests', label: 'Requests', icon: UserPlus, count: receivedRequests.length },
    { id: 'sent', label: 'Sent', icon: Clock, count: sentRequests.length },
  ];

  return (
    <div className={embedded ? '' : 'min-h-screen bg-zinc-50'}>
      {!embedded && (
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-zinc-700">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>People</span>
          </div>
          <div className="flex items-center gap-4">
            <ChatButton />
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </header>
      )}

      <main className={embedded ? '' : 'container mx-auto px-4 sm:px-6 py-4 sm:py-6 max-w-3xl'}>
        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-zinc-200 mb-6" data-testid="people-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? 'bg-orange-500 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
              {t.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  tab === t.id ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-600'
                }`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Search (Discover Tab) */}
        {tab === 'discover' && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search people by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="people-search-input"
              />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : (
          <>
            {/* Discover Tab */}
            {tab === 'discover' && (
              <div className="space-y-2" data-testid="discover-list">
                {people.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <Search className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-500">Search for people to connect with</p>
                  </div>
                ) : people.map(person => {
                  const RoleIcon = getRoleIcon(person.role);
                  return (
                    <div key={person.id} className="bg-white rounded-xl border border-zinc-200 p-4 flex items-center gap-4" data-testid={`person-${person.id}`}>
                      <div className="relative">
                        <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center">
                          <RoleIcon className="w-6 h-6 text-zinc-500" />
                        </div>
                        {person.is_online && (
                          <Circle className="w-3.5 h-3.5 text-green-500 fill-green-500 absolute -bottom-0.5 -right-0.5 border-2 border-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-zinc-900 truncate">{person.name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadge(person.role)}`}>{person.role}</span>
                        </div>
                        <p className="text-xs text-zinc-400">{person.is_online ? 'Online' : 'Offline'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {person.friendship_status === 'friends' ? (
                          <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                            <UserCheck className="w-4 h-4" /> Friends
                          </span>
                        ) : person.friendship_status === 'pending_sent' ? (
                          <span className="text-sm text-zinc-400 font-medium">Pending</span>
                        ) : person.friendship_status === 'pending_received' ? (
                          <Button
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            disabled={actionLoading[person.id]}
                            onClick={() => {
                              // Need to find the request ID - we'll accept from requests tab
                              toast.info('Check Requests tab to accept');
                            }}
                            data-testid={`accept-${person.id}`}
                          >
                            Accept
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-200 text-orange-600 hover:bg-orange-50"
                            disabled={actionLoading[person.id]}
                            onClick={() => handleAction(person.id, () => friendAPI.sendRequest(person.id))}
                            data-testid={`add-friend-${person.id}`}
                          >
                            {actionLoading[person.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Add</>}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Friends Tab */}
            {tab === 'friends' && (
              <div className="space-y-2" data-testid="friends-list">
                {friends.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <Users className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-500">No friends yet</p>
                    <p className="text-sm text-zinc-400 mt-1">Search and send friend requests to connect</p>
                  </div>
                ) : friends.map(friend => {
                  const RoleIcon = getRoleIcon(friend.friend_role);
                  return (
                    <div key={friend.id} className="bg-white rounded-xl border border-zinc-200 p-4 flex items-center gap-4" data-testid={`friend-${friend.friend_id}`}>
                      <div className="relative">
                        <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center">
                          <RoleIcon className="w-6 h-6 text-zinc-500" />
                        </div>
                        {friend.is_online && (
                          <Circle className="w-3.5 h-3.5 text-green-500 fill-green-500 absolute -bottom-0.5 -right-0.5 border-2 border-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-zinc-900">{friend.friend_name}</p>
                        <p className="text-xs text-zinc-400">
                          {friend.is_online ? (
                            <span className="text-green-600">Online</span>
                          ) : friend.last_seen ? (
                            `Last seen ${new Date(friend.last_seen).toLocaleString()}`
                          ) : 'Offline'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-orange-500"
                          onClick={() => navigate('/messages')}
                          data-testid={`msg-friend-${friend.friend_id}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-600"
                          disabled={actionLoading[friend.friend_id]}
                          onClick={() => handleAction(friend.friend_id, () => friendAPI.removeFriend(friend.friend_id))}
                          data-testid={`remove-friend-${friend.friend_id}`}
                        >
                          <UserX className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Received Requests Tab */}
            {tab === 'requests' && (
              <div className="space-y-2" data-testid="requests-list">
                {receivedRequests.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <UserPlus className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-500">No pending friend requests</p>
                  </div>
                ) : receivedRequests.map(req => {
                  const RoleIcon = getRoleIcon(req.sender_role);
                  return (
                    <div key={req.id} className="bg-white rounded-xl border border-zinc-200 p-4 flex items-center gap-4" data-testid={`request-${req.id}`}>
                      <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center">
                        <RoleIcon className="w-6 h-6 text-zinc-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-zinc-900">{req.sender_name}</p>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadge(req.sender_role)}`}>{req.sender_role}</span>
                          <span className="text-xs text-zinc-400">{new Date(req.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          disabled={actionLoading[req.id]}
                          onClick={() => { handleAction(req.id, () => friendAPI.acceptRequest(req.id)); toast.success(`You and ${req.sender_name} are now friends!`); }}
                          data-testid={`accept-req-${req.id}`}
                        >
                          {actionLoading[req.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Accept</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-zinc-500"
                          disabled={actionLoading[`dec-${req.id}`]}
                          onClick={() => handleAction(`dec-${req.id}`, () => friendAPI.declineRequest(req.id))}
                          data-testid={`decline-req-${req.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sent Requests Tab */}
            {tab === 'sent' && (
              <div className="space-y-2" data-testid="sent-list">
                {sentRequests.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <Clock className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-500">No pending sent requests</p>
                  </div>
                ) : sentRequests.map(req => (
                  <div key={req.id} className="bg-white rounded-xl border border-zinc-200 p-4 flex items-center gap-4" data-testid={`sent-${req.id}`}>
                    <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-zinc-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-zinc-900">{req.receiver_name}</p>
                      <p className="text-xs text-zinc-400">Sent {new Date(req.created_at).toLocaleDateString()}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-600"
                      disabled={actionLoading[req.id]}
                      onClick={() => { handleAction(req.id, () => friendAPI.cancelRequest(req.id)); toast.success('Request cancelled'); }}
                      data-testid={`cancel-req-${req.id}`}
                    >
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default People;
